'use strict';

const FORM_SELECTOR = '[data-validate]';
const FIELD_SELECTOR = 'input, select, textarea';
const ERROR_SELECTOR = '[data-validation-error]';

const DEFAULT_MESSAGES = {
    valueMissing: 'This field is required',
    typeMismatch: {
        email: 'Enter an email address in the correct format, like name@example.com',
        url: 'Enter a web address in the correct format, like https://example.com',
        default: 'Enter a value in the correct format',
    },
    patternMismatch: 'Enter a value in the correct format',
    tooShort: 'Enter at least {minlength} characters',
    tooLong: 'Enter no more than {maxlength} characters',
    rangeUnderflow: 'Enter a value of {min} or more',
    rangeOverflow: 'Enter a value of {max} or less',
    stepMismatch: 'Enter a valid value',
    badInput: 'Enter a valid value',
};

const getErrorElement = field => {
    const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);

    return ids
        .map(id => document.getElementById(id))
        .find(el => el && el.matches(ERROR_SELECTOR));
};

const interpolate = (message, field) => {
    return message.replace(/\{(\w+)\}/g, (match, attr) => field.hasAttribute(attr) ? field.getAttribute(attr) : match);
};

const getErrorMessage = field => {
    const { validity, dataset } = field;

    if (validity.valid) {
        return '';
    }

    if (validity.valueMissing) {
        return dataset.errorRequired || DEFAULT_MESSAGES.valueMissing;
    }

    if (validity.typeMismatch) {
        const typeMessages = DEFAULT_MESSAGES.typeMismatch;
        return dataset.errorType || typeMessages[field.type] || typeMessages.default;
    }

    const failedKey = Object.keys(DEFAULT_MESSAGES).find(key => validity[key]);

    if (failedKey) {
        return interpolate(dataset.errorInvalid || DEFAULT_MESSAGES[failedKey], field);
    }

    return dataset.errorInvalid || field.validationMessage;
};

const showError = (field, errorEl, message) => {
    errorEl.textContent = message;
    errorEl.hidden = false;
    field.setAttribute('aria-invalid', 'true');
};

const clearError = (field, errorEl) => {
    errorEl.textContent = '';
    errorEl.hidden = true;
    field.removeAttribute('aria-invalid');
};

const validateField = field => {
    const errorEl = getErrorElement(field);

    if (!errorEl) {
        return field.validity.valid;
    }

    const message = getErrorMessage(field);

    if (message) {
        showError(field, errorEl, message);
        return false;
    }

    clearError(field, errorEl);
    return true;
};

const setupForm = form => {
    const fields = Array.from(form.querySelectorAll(FIELD_SELECTOR)).filter(getErrorElement);

    if (!fields.length) {
        return;
    }

    form.noValidate = true;

    form.addEventListener('submit', event => {
        const invalidFields = fields.filter(field => !validateField(field));

        if (invalidFields.length) {
            event.preventDefault();
            invalidFields[0].focus();
        }
    });

    fields.forEach(field => {
        const eventName = field.matches('select, [type="checkbox"], [type="radio"]') ? 'change' : 'input';

        field.addEventListener(eventName, () => {
            if (field.getAttribute('aria-invalid') === 'true') {
                validateField(field);
            }
        });
    });
};

export function init() {
    document.querySelectorAll(FORM_SELECTOR).forEach(setupForm);
}
